// Avatar Management Utilities
// Extracted from BattleUI.js for reusability across the app

/**
 * Get avatar image path from avatar name
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Image path for the avatar
 */
export function getAvatarImage(avatarName) {
  if (!avatarName) return '/images/human_1.png';
  const normalizedName = avatarName.replace(/\s+/g, '_');
  if (/\.(png|jpg|jpeg|webp)$/i.test(normalizedName)) {
    return `/images/${normalizedName}`;
  }
  return `/images/${normalizedName}.png`;
}

/**
 * Get avatar direction from avatar data
 * @param {string} avatarName - Name of the avatar
 * @param {Array} avatars - Array of avatar data
 * @returns {string} - Direction ('left' or 'right')
 */
export function getAvatarDirection(avatarName, avatars) {
  if (!avatarName) return 'right';
  // ✅ FIX: Case-insensitive lookup to handle avatar name variations
  const avatar = avatars.find(a => 
    a.avatar_name && avatarName && 
    a.avatar_name.toLowerCase() === avatarName.toLowerCase()
  );
  return avatar?.view_direction || 'right';
}

/**
 * Determine if avatar should be mirrored based on position
 * @param {string} avatarName - Name of the avatar
 * @param {string} position - Position ('left' or 'right')
 * @param {Array} avatars - Array of avatar data
 * @returns {boolean} - True if avatar should be mirrored
 */
export function shouldMirrorAvatar(avatarName, position, avatars) {
  const direction = getAvatarDirection(avatarName, avatars);
  // Left position should face right, right position should face left
  if (position === 'left') {
    return direction === 'left'; // If avatar faces left, mirror it to face right
  } else {
    return direction === 'right'; // If avatar faces right, mirror it to face left
  }
}

/**
 * Get opponent avatar image path (same as regular avatar)
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Image path for the avatar
 */
export function getOpponentAvatarImage(avatarName) {
  if (!avatarName) return '/images/human_1.png';
  const normalizedName = avatarName.replace(/\s+/g, '_');
  if (/\.(png|jpg|jpeg|webp)$/i.test(normalizedName)) {
    return `/images/${normalizedName}`;
  }
  return `/images/${normalizedName}.png`;
}

/**
 * Determine if opponent avatar should be mirrored based on position and type
 * @param {string} avatarName - Name of the avatar
 * @param {string} position - Position (e.g., 'upper_right')
 * @param {string} avatarType - Type of avatar ('general' or 'advisor')
 * @param {Array} avatars - Array of avatar data
 * @returns {boolean} - True if avatar should be mirrored
 */
export function shouldMirrorOpponentAvatar(avatarName, position, avatarType, avatars) {
  // Opponent avatars in upper middle or upper right:
  // - General (left in the pair): should face right (same as player left)
  // - Advisor (right in the pair): should face left (same as player right)
  if (position === 'upper_right' || position === 'upper_middle') {
    if (avatarType === 'general') {
      // General is on the left, should face right (same as player left)
      return shouldMirrorAvatar(avatarName, 'left', avatars);
    } else if (avatarType === 'advisor') {
      // Advisor is on the right, should face left (same as player right)
      return shouldMirrorAvatar(avatarName, 'right', avatars);
    }
  }
  return false;
}

/**
 * Get default avatar image path
 * @returns {string} - Default avatar image path
 */
export function getDefaultAvatarImage() {
  return '/images/human_1.png';
}

/**
 * Format avatar name for file path
 * @param {string} avatarName - Name of the avatar
 * @returns {string} - Formatted name for file path
 */
export function formatAvatarName(avatarName) {
  if (!avatarName) return 'human_1';
  return avatarName.toLowerCase().replace(/\s+/g, '_');
}
